import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import type {
  BedrockModelRole,
  BedrockModelRoleBinding,
} from "../OrgLlmProviderRecord";

import { orgQueryKeys } from "./orgQueryKeys";

/** Every deployment-wide model role and the catalog entry bound to it (Roboto admins). */
export function useBedrockRoles() {
  const { orgService } = useDomainServices();

  return useQuery<BedrockModelRoleBinding[], Error>({
    queryKey: orgQueryKeys.bedrockRoles(),
    queryFn: ({ signal }) =>
      orgService.getBedrockRoles({ abortSignal: signal }),
  });
}

/**
 * Bind a role to a catalog entry, or unset it with `null` (Roboto admins).
 * Refetches the roles and the catalog, whose entries list the roles they
 * are bound to.
 */
export function useUpdateBedrockRole() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    BedrockModelRoleBinding,
    Error,
    { role: BedrockModelRole; model: string | null }
  >({
    mutationFn: ({ role, model }) => orgService.updateBedrockRole(role, model),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orgQueryKeys.bedrockRoles(),
      });
      void queryClient.invalidateQueries({
        queryKey: orgQueryKeys.bedrockCatalog(),
      });
    },
  });
}
